import * as React from "react";
import InstallationModel from "../../core/models/Installation";
import {
  Theme,
  makeStyles,
  createStyles,
  Tooltip,
  Typography,
  IconButton,
  AccordionSummary,
  Accordion,
  AccordionDetails,
} from "@material-ui/core";
import TruckIcon from "../../assets/icons/TruckIcon";
import { formatDateTime } from "../../helpers/datetime";
import TankIndicator from "../TankIndicator";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import clsx from "clsx";
import SimpleLink from "../SimpleLink";
import TankModel from "../../core/models/Tank";
import { LevelThreshold } from "../../core/models/LevelThreshold";
import {
  calculateLevelPercentage,
  getLevelStatusString,
  getLevelColor,
  checkForLevelDataDelay,
  checkTripPlanned,
  checkTripInProgress,
  RenderInstallationTankWarningsTooltip,
  BuildDelayErrorTooltip,
  RenderTanksWarnings,
} from "../../helpers/installationTankHelpers";
import { formatValue, isArrayValid } from "../../helpers/utility";
import { levelStatus } from "../../core/models/LevelStatus";
import ErrorIcon from "@material-ui/icons/Error";
import PauseIcon from "@material-ui/icons/PauseCircleFilled";
import { useHubXValue } from "../../core/hooks/useHubHooks";
import { EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE } from "../../Events";

const useStyles = makeStyles((theme: Theme) => {
  const borderPallet = `0.4px solid ${theme.customPalette.border.lightgray}`;

  return createStyles({
    root: {
      "& .MuiAccordion-root.Mui-expanded": {
        margin: 0,
      },
    },

    gridRow: {
      "display": "grid",
      "gridTemplateColumns": "2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1fr 1fr 0.75fr 0.5fr",
      "textAlign": "center",
      "alignItems": "center",
      "columnGap": `${theme.spacing(1)}px`,
      "padding": theme.spacing(0, 1),
      "color": theme.palette.text.secondary,
      "minHeight": 42,
      // 1024px override
      [theme.breakpoints.down("md")]: {
        padding: 0,
        gridTemplateColumns: "2fr 1.5fr 1fr 4fr 2fr 2fr 2fr 1.5fr 1.6fr 1.6fr 0.95fr 0.5fr",
        fontSize: "0.7rem",
      },

      "&:not(.header)": {
        "&:not(.secondary)": {
          backgroundColor: theme.customPaper.background.cardBackgroundTable,
          border: borderPallet,
        },

        "& > *:not(.last)": {
          borderRight: borderPallet,
          height: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },

        "&.secondary": {
          "&:not(last-child)": {
            borderBottom: borderPallet,
          },
          // override borderRight from no secondary theme
          "& > *:not(.last)": {
            borderRight: "transparent",
          },
        },
      },

      "& .header": {
        backgroundColor: theme.palette.common.white,
      },

      "& .date-wrapper": {
        "& > *:last-child": {
          marginLeft: theme.spacing(0.5),
        },
      },

      "& .MuiTypography-body1": {
        fontSize: "0.875rem",
        [theme.breakpoints.down("md")]: {
          fontSize: "0.8rem",
        },
      },

      "& .baseWidth": {
        minWidth: "10%",
      },

      "& .label-flex-wrapper": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        flexWrap: "wrap",
        flex: 1,
      },

      "& .bold": {
        "& > *": {
          fontWeight: "bolder",
        },
      },

      "& .opaque": {
        opacity: 0.5,
      },

      "& .levelIndicatorWrapper": {
        "padding": theme.spacing(0, 1),
        "& > span": {
          marginLeft: theme.spacing(2),
          fontWeight: "bold",
        },
        "&:not(.secondary) > span": {
          fontSize: "1.25em",
          color: "#1D212A",
        },
      },

      "& .disable-icon-padding": {
        "& *": {
          padding: 0,
        },
      },

      "& .name": {
        fontSize: "0.7rem",
        maxHeight: 42,
        whiteSpace: "break-spaces",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },

      "& .max-width": {
        maxWidth: 90,
      },
    },

    accordion: {
      "& .MuiAccordionSummary-root": {
        padding: 0,
        minHeight: "unset",
      },

      "& .MuiAccordionSummary-content": {
        "margin": 0,

        "& > *": {
          width: "100%",
        },
      },
    },

    icon: {
      "& .MuiSvgIcon-root": {
        fontSize: "2.5rem",
        [theme.breakpoints.down("sm")]: {
          fontSize: "2rem",
        },
      },
    },

    tanksWrapper: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
    },

    opaque: {
      color: "transparent",
    },

    hidden: {
      // display: "none !important",
      "color": "transparent",
      "&*": {
        color: "transparent",
      },
      "& *": {
        color: "transparent",
      },

      "& > *": {
        display: "none",
      },
    },
  });
});

type HomeTableDataType = {
  id: string | number;
  name?: string;
  linkLabel?: string;
  link?: string;
  level?: number;
  status?: levelStatus;
  uom?: string;
  capacity?: number;
  thresholds: LevelThreshold;
  lastReading?: Date;
  plannedTrip?: boolean;
  tripInProgress?: boolean;
  numberOfThanks?: number;
  Tanks?: TankModel[];
};

type HomeTableRowProps = {
  data: HomeTableDataType;
  isTank?: boolean;
};

export const HomeTableColumns = [
  "Nome Fantasia",
  "Nível",
  "Nível Instântaneo",
  "Capacidade Total",
  "Status Nível",
  "Última Medição",
  "Viagem Planejada",
  "Viagem Em Rota",
  "Tanques",
] as const;

export type HomeTableColumnsType = typeof HomeTableColumns[number];

// FIXME - Melhorar o comportamento e otimizar componentes

function HomeTableRow({ data, isTank = false }: HomeTableRowProps) {
  const [isExpanded, setExpanded] = React.useState(false);
  const [nowDate, setNowDate] = React.useState(new Date());

  const delay = checkForLevelDataDelay(data.lastReading);

  function HomeTableRowMain() {
    const showColumns = useHubXValue(
      EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE,
      HomeTableColumns,
      true
    );

    const warnings = isTank
      ? RenderInstallationTankWarningsTooltip(
        {
          lastReadingDate: data.lastReading && new Date(data.lastReading),
          level: data.level,
          levelStatus: data.status,
          capacity: data.thresholds.over,
          veryLowLevel: data.thresholds.veryLow,
          lowLevel: data.thresholds.low,
        },
        nowDate
      )
      : delay && data.lastReading
        ? BuildDelayErrorTooltip({ start: data.lastReading, end: nowDate })
        : undefined;

    const isSingleTank = data.Tanks?.length === 1;

    const tanksWarnings =
      data.Tanks && !isSingleTank ? RenderTanksWarnings(data.Tanks, nowDate) : undefined;

    const showName = showColumns.includes("Nome Fantasia");
    const showLevel = showColumns.includes("Nível");
    const showPlannedIcon = showColumns.includes("Viagem Planejada");
    const showTravelIcon = showColumns.includes("Viagem Em Rota");
    const showTanks = showColumns.includes("Tanques");

    return (
      <div className={clsx(isTank && "secondary", classes.gridRow)}>
        <div className="baseWidth">
          {data.link && data.linkLabel && (
            <SimpleLink
              label={data?.linkLabel}
              href={data?.link}
              tooltip="clique aqui para ser direcionado à tela de detalhes desta instalação"
            />
          )}
        </div>
        <div className={clsx(!showName && classes.hidden)}>
          <Tooltip title={(showName && data.name) || ""}>
            <Typography className="name">{data.name}</Typography>
          </Tooltip>
        </div>
        <div className="max-width">
          <Typography>{data.id}</Typography>
        </div>
        <div
          className={clsx(
            isTank && "secondary",
            delay && "opaque",
            "levelIndicatorWrapper",
            !showLevel && classes.hidden
          )}
        >
          {showLevel && (
            <>
              {warnings ? (
                <Tooltip onOpen={() => setNowDate(new Date())} title={warnings}>
                  {delay ? (
                    <PauseIcon color="error" fontSize="small" />
                  ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                </Tooltip>
              ) : (
                  <ErrorIcon className={classes.opaque} fontSize="small" />
                )}
              <TankIndicator
                status={data.status}
                capacity={data.capacity}
                level={data.level}
                thresholds={data.thresholds}
              />
              <span>{calculateLevelPercentage(data.level, data.capacity)}%</span>
            </>
          )}
        </div>
        <div
          className={clsx(
            "baseWidth",
            !showColumns.includes("Nível Instântaneo") && classes.hidden
          )}
        >
          <div className="label-flex-wrapper">
            <Typography>{`${formatValue(data.level)}${data?.uom ? ` ${data.uom}` : ""
              }`}</Typography>
          </div>
        </div>
        <div
          className={clsx("baseWidth", !showColumns.includes("Capacidade Total") && classes.hidden)}
        >
          <Typography>{`${formatValue(data.capacity)}${data?.uom ? ` ${data.uom}` : ""
            }`}</Typography>
        </div>
        <div
          className={clsx(
            "baseWidth",
            !isTank && "bold",
            !showColumns.includes("Status Nível") && classes.hidden
          )}
          style={{ color: getLevelColor(data.status || data.level, data.thresholds) }}
        >
          <Typography>
            {getLevelStatusString(data.status || data.level, data.thresholds)}
          </Typography>
        </div>
        <div
          className={clsx(
            "date-wrapper",
            !showColumns.includes("Última Medição") && classes.hidden
          )}
        >
          <Typography color={delay ? "error" : "inherit"}>
            {formatDateTime(data.lastReading)}
          </Typography>
        </div>
        <div className={clsx(classes.icon, !showPlannedIcon && classes.hidden)}>
          {data?.plannedTrip && (
            <Tooltip title={showPlannedIcon && "A próxima viagem de abastecimento está prevista"}>
              <span>
                <TruckIcon scale={1.2} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={clsx(classes.icon, !showTravelIcon && classes.hidden)}>
          {data?.tripInProgress && (
            <Tooltip title={showTravelIcon && "A viagem de abastecimento está em rota"}>
              <span>
                <TruckIcon altVersion color="primary" scale={1.2} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={clsx(!showTanks && classes.hidden)}>
          <Typography>{data?.numberOfThanks}</Typography>
          {tanksWarnings && (
            <Tooltip
              onOpen={() => showTanks && setNowDate(new Date())}
              title={showTanks && tanksWarnings}
            >
              <ErrorIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </div>
        <div className={clsx("last", "disable-icon-padding")}>
          {data.Tanks && (
            <Tooltip
              title={
                isSingleTank
                  ? ""
                  : isExpanded
                    ? "clique para colapsar a linha"
                    : "clique para expandir a linha e exibir os detalhes dos tanques"
              }
            >
              <span>
                <IconButton disabled={isSingleTank}>
                  {!isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  const classes = useStyles();

  if (isArrayValid(data.Tanks) && data?.Tanks?.length && data?.Tanks?.length > 1) {
    return (
      <Accordion
        elevation={0}
        expanded={isExpanded}
        onChange={() => setExpanded((prev) => !prev)}
        key={data.id}
        className={classes.accordion}
        TransitionProps={{ unmountOnExit: true }}
      >
        <AccordionSummary expandIcon={null}>{HomeTableRowMain()}</AccordionSummary>

        <AccordionDetails>
          <div className={classes.tanksWrapper}>
            {data?.Tanks?.map((tank) => (
              <HomeTableRow
                key={tank.tank_id}
                data={{
                  id: tank.tank_id,
                  level: tank.tank_level_su,
                  capacity: tank.tank_capacity_su,
                  uom: tank.product_uom,
                  thresholds: {
                    veryLow: tank.outage_level,
                    over: tank.tank_capacity_su,
                  },
                  lastReading: tank?.last_rtu_reading_date
                    ? new Date(tank.last_rtu_reading_date)
                    : undefined,
                  status: tank.tank_level_status,
                }}
                isTank
              />
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    );
  }

  return HomeTableRowMain();
}

type HomeTable = {
  installations?: InstallationModel[];
  customerId?: string;
};

function HomeTable({ installations, customerId }: HomeTable) {
  const classes = useStyles();
  const showColumns = useHubXValue(
    EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE,
    HomeTableColumns
  );

  const renderHeader = () => {
    return (
      <div className={clsx(classes.gridRow, "header")}>
        <div className="baseWidth">
          <Typography>Produto</Typography>
        </div>
        <div className={clsx(!showColumns.includes("Nome Fantasia") && classes.hidden)}>
          <Typography>Nome Fantasia</Typography>
        </div>
        <div>
          <Typography>ID</Typography>
        </div>
        <div className={clsx(!showColumns.includes("Nível") && classes.hidden)}>
          <Typography>Nível</Typography>
        </div>
        <div
          className={clsx(
            "baseWidth",
            !showColumns.includes("Nível Instântaneo") && classes.hidden
          )}
        >
          <Typography>Nível Instântaneo</Typography>
        </div>
        <div
          className={clsx("baseWidth", !showColumns.includes("Capacidade Total") && classes.hidden)}
        >
          <Typography>Capacidade Total</Typography>
        </div>
        <div className={clsx("baseWidth", !showColumns.includes("Status Nível") && classes.hidden)}>
          <Typography>Status Nível</Typography>
        </div>
        <div className={clsx(!showColumns.includes("Última Medição") && classes.hidden)}>
          <Typography>Última Medição</Typography>
        </div>
        <div className={clsx(!showColumns.includes("Viagem Planejada") && classes.hidden)}>
          <Typography>Viagem Planejada</Typography>
        </div>
        <div className={clsx(!showColumns.includes("Viagem Em Rota") && classes.hidden)}>
          <Typography>Viagem Em Rota</Typography>
        </div>
        <div className={clsx(!showColumns.includes("Tanques") && classes.hidden)}>
          <Typography>Tanques</Typography>
        </div>
        {/* Placeholder for expand icon */}
        <div />
      </div>
    );
  };

  return (
    <div className={classes.root}>
      {/* Table Header */}
      {renderHeader()}
      {/* Table installation Rows */}
      {installations?.map((installation) => (
        <HomeTableRow
          key={installation.installation_id}
          data={{
            id: installation.installation_id,
            name: installation.name,
            linkLabel: installation.product_code,
            link: `/installation/${customerId}/${installation.installation_id}`,
            level: installation.installation_level,
            uom: installation.product_uom,
            capacity: installation.inventory_capacity_su,
            thresholds: {
              veryLow: installation.outage_level_su,
              low: installation.reserve_level_su,
              over: installation.inventory_capacity_su,
            },
            lastReading: installation.last_rtu_reading_date
              ? new Date(installation.last_rtu_reading_date)
              : undefined,
            plannedTrip: checkTripPlanned(installation.trip_status),
            tripInProgress: checkTripInProgress(installation.trip_status),
            numberOfThanks: installation.number_of_tanks,
            Tanks: installation.tanks,
            status: installation.installation_level_status,
          }}
        />
      ))}
    </div>
  );
}

export default HomeTable;
